import { Descendant } from "slate";
import {
    parseTokenString,
    VariableTextSegment,
} from "@common/helpers/variable-field";
import { CustomText } from "@components/mainInterface/chat/slate/slateTypes";
import { VariableElement } from "./variableTextTypes";

/** Escapes `\{}` so literal text round-trips and isn't parsed as a variable. */
function escapeLiteralText(text: string): string {
    return text.replace(/[\\{}]/g, (match) => `\\${match}`);
}

export function serializeSegments(segments: VariableTextSegment[]): string {
    return segments
        .map((segment) =>
            segment.kind === "variable"
                ? `{{${segment.variableKey}}}`
                : escapeLiteralText(segment.value),
        )
        .join("");
}

function segmentsToParagraphChildren(
    segments: VariableTextSegment[],
): Array<CustomText | VariableElement> {
    const children: Array<CustomText | VariableElement> = [{ text: "" }];

    for (const segment of segments) {
        if (segment.kind === "variable") {
            children.push({
                type: "variable",
                variableKey: segment.variableKey,
                children: [{ text: "" }],
            });
            // A void inline must be followed by a text node so the caret has a
            // landing spot and adjacent atoms stay separable.
            children.push({ text: "" });
            continue;
        }

        const last = children[children.length - 1];
        if ("text" in last) {
            last.text += segment.value;
        } else {
            children.push({ text: segment.value });
        }
    }

    return children;
}

/** Builds one Slate paragraph per stored line so multiline fields round-trip. */
export function tokenStringToSlate(
    value: string,
    isKnownKey: (key: string) => boolean,
): Descendant[] {
    return value.split("\n").map((line) => ({
        type: "paragraph",
        children: segmentsToParagraphChildren(
            parseTokenString(line, isKnownKey),
        ),
    }));
}

export function slateToTokenString(nodes: Descendant[]): string {
    const lines: string[] = [];

    for (const node of nodes) {
        if (!("children" in node)) {
            continue;
        }
        let line = "";
        for (const child of node.children) {
            if ("text" in child) {
                line += escapeLiteralText(child.text);
            } else if (child.type === "variable") {
                line += `{{${child.variableKey}}}`;
            }
        }
        lines.push(line);
    }

    return lines.join("\n");
}
